"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert, Shield, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const StyledInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-10 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50 focus-visible:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
StyledInput.displayName = "StyledInput";
const StyledTextarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[80px] w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 ring-offset-background placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50 focus-visible:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
StyledTextarea.displayName = "StyledTextarea";
const StyledSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  ...props
}, ref) => {
  return <div data-section-id="63170" className="relative">
        <select className={`flex h-10 w-full appearance-none rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50 focus-visible:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
          <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>;
});
StyledSelect.displayName = "StyledSelect";
function CalculationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-white/10 backdrop-blur-md rounded-sm border border-white/20 animate-in fade-in zoom-in duration-300">
        <div className="w-16 h-16 bg-emerald-700 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-emerald-900/30">
          <CircleCheck className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-white mb-2">Заявка принята</h3>
        <p className="text-slate-200 mb-6">{successMessage}</p>
        <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 font-sans" onClick={() => window.location.reload()}>
          Вернуться к форме
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Укажите ваше имя"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-200 font-sans">Ваше имя</FormLabel>
              <FormControl>
                <StyledInput placeholder="Константин Александрович" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Укажите email",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Некорректный адрес"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-200 font-sans">Электронная почта</FormLabel>
              <FormControl>
                <StyledInput type="email" placeholder="example@mail.ru" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Укажите телефон"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-200 font-sans">Контактный телефон</FormLabel>
              <FormControl>
                <StyledInput type="tel" placeholder="+7 (___) ___-__-__" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-200 font-sans">Организация (для юрлиц)</FormLabel>
              <FormControl>
                <StyledInput placeholder="ООО «Право»" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-200 font-sans">Характер дела</FormLabel>
            <FormControl>
              <StyledSelect {...field}>
                <option value="" disabled>Выберите категорию</option>
                <option value="civil">Гражданское право</option>
                <option value="arbitration">Арбитражные споры</option>
                <option value="family">Семейные споры</option>
                <option value="inheritance">Наследственные дела</option>
              </StyledSelect>
            </FormControl>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      <FormField control={control} name="start_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-200 font-sans">Предпочтительная дата консультации</FormLabel>
            <FormControl>
              <StyledInput type="date" {...field} />
            </FormControl>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      <FormField control={control} name="project_details" rules={{
      required: "Кратко опишите ситуацию"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-slate-200 font-sans">Суть вопроса</FormLabel>
            <FormControl>
              <StyledTextarea placeholder="Опишите обстоятельства дела и желаемый результат..." className="min-h-[120px]" {...field} />
            </FormControl>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-900/20 border border-red-900/40 rounded-sm flex items-start gap-3">
          <CircleAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <p className="text-red-200 text-sm font-sans">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold bg-[#1E3A8A] hover:bg-[#0A192F] text-white border-none shadow-lg shadow-black/20 transition-all hover:scale-[1.01] active:scale-[0.99] animate-pulse hover:animate-none font-sans">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Обработка...
          </> : <>
            Записаться на консультацию
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
      
      <p className="text-[10px] text-slate-400 text-center mt-4 opacity-80 font-sans uppercase tracking-wider">
        Отправляя форму, вы подтверждаете согласие на обработку персональных данных.
      </p>
    </Form>;
}
export default function CalculationRequest() {
  return <section id="записаться-на-консультацию" className="dark bg-[#0A192F] text-foreground py-20 lg:py-28 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-1/2 h-full bg-slate-800/10 mix-blend-multiply"></div>
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-slate-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#1E3A8A]/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/grid_pattern.png')] opacity-[0.02] bg-repeat"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {}
          <div className="space-y-8 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-sm bg-white/5 border border-white/10 text-xs font-mono tracking-widest text-slate-300 backdrop-blur-sm uppercase">
              <Shield className="w-4 h-4 text-[#1E3A8A]" />
              <span>Реестровый номер № 40/806</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight tracking-tight text-white">
              Надежная защита <span className="text-slate-400 italic">ваших</span> законных интересов
            </h2>
            
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed font-sans font-light">
              Адвокат Николай Кондрашов: 11 лет успешной практики в Калуге. Профессиональный подход к решению сложных правовых задач в гражданском и арбитражном праве.
            </p>

            <div className="flex flex-col sm:flex-row gap-8 pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-sm bg-white/5 flex items-center justify-center shrink-0 border border-white/10">
                  <span className="text-2xl font-serif font-bold text-[#1E3A8A]">11</span>
                </div>
                <div>
                  <h4 className="font-serif font-bold text-lg text-white">Лет практики</h4>
                  <p className="text-slate-400 text-sm font-sans">Богатый опыт ведения дел</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-full flex flex-col">
                  <h4 className="font-serif font-bold text-lg text-white">Прямая связь</h4>
                  <p className="text-2xl md:text-3xl font-serif text-[#1E3A8A] mt-1">+7 (995) 305-79-51</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="w-full">
            <div className="bg-slate-900/40 backdrop-blur-md p-6 md:p-10 rounded-sm border border-white/10 shadow-2xl shadow-black/50">
              <div className="mb-8 border-b border-white/10 pb-6">
                <h3 className="text-2xl font-serif font-bold text-white uppercase tracking-wide">Запись на прием</h3>
                <p className="text-slate-400 text-sm mt-2 font-sans">Оставьте свои контакты, и я свяжусь с вами в течение рабочего дня.</p>
              </div>
              
              <FormProvider formKey="consultation_form_home" formId="consultation_form_home" sectionName="consultation-request" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              project_details: "",
              budget: "",
              start_date: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              project_details: {
                required: true
              }
            }} submitText="Записаться на консультацию" successMessage="Ваше сообщение получено. Я проанализирую ситуацию и перезвоню вам в ближайшее время." errorMessage="Ошибка отправки. Пожалуйста, свяжитесь со мной по телефону.">
                <CalculationFormContent />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}